/**
 * API Configuration
 * Single source of truth for API settings
 */

// API Endpoints - organized by module
export const API_ENDPOINTS = {
  leadForms: { list: "marketing/lead-forms", create: "marketing/lead-forms" },
  objectStorage: {
    upload: "/file/upload",
  },
  content: {
    folders: "folders",
    folderByKey: (folderKey: string) =>
      `folders/${encodeURIComponent(folderKey)}`,
    renameFolder: (folderKey: string) =>
      `folders/${encodeURIComponent(folderKey)}/rename`,
    moveFolder: (folderKey: string) =>
      `folders/${encodeURIComponent(folderKey)}/move`,
    bulkMoveFolders: "folders/bulk/move",
    bulkDeleteFolders: "folders/bulk/delete",
    bulkMoveFilesToFolder: "folders/files/bulk/move",
    moveFileToFolder: (fileKey: string) =>
      `folders/files/${encodeURIComponent(fileKey)}/move`,
    files: "files",
    storageUsage: "storage/usage",
    uploadSessions: "uploads/sessions",
    completeUploadSession: (uploadKey: string) =>
      `uploads/sessions/${encodeURIComponent(uploadKey)}/complete?includeAccess=true`,
    resolveFileReference: "files/resolve-reference",
    trashFiles: "files/trash",
    upsertFileUsage: (fileKey: string) =>
      `files/${encodeURIComponent(fileKey)}/usages`,
    moveFileToTrash: (fileKey: string) =>
      `files/${encodeURIComponent(fileKey)}/trash`,
    bulkMoveFilesToTrash: "files/bulk/trash",
    restoreFileFromTrash: (fileKey: string) =>
      `files/${encodeURIComponent(fileKey)}/restore`,
    bulkRestoreFilesFromTrash: "files/bulk/restore",
    bulkPurgeFiles: "files/bulk/purge",
    purgeFile: (fileKey: string) => `files/${encodeURIComponent(fileKey)}/purge`,
  },
  auth: {
    signup: "/auth/signup",
    checkLogin: "/auth/login/check?identifier=",
    verifyLogin: "/auth/login/verify",
    logout: "/auth/logout",
    logoutAll: "/auth/logout-all",
    sessions: "/auth/sessions",
    sessionLogout: (sessionId: string) =>
      `/auth/sessions/${encodeURIComponent(sessionId)}/logout`,
    otpGenerate: "/auth/otp/generate",
    otpVerify: "/auth/otp/verify",
    forgotPassword: "/auth/forget",
    resetPassword: "/auth/reset",
    mySecurity: "/auth/me/security",
    myPassword: "/auth/me/password",
    myAccountMembership: "/auth/me/account-membership",
    refreshToken: "/auth/refreshToken",
    googleOAuth: "/auth/oauth/google",
    googleOAuthCallback: "/auth/oauth/google/callback",
    googleOAuthAgentCallback: "/auth/oauth/google/agent/callback",
    microsoftOAuth: "/auth/oauth/microsoft",
    microsoftOAuthCallback: "/auth/oauth/microsoft/callback",
    microsoftOAuthAgentCallback: "/auth/oauth/microsoft/agent/callback",
    verifyTurnstile: "/auth/turnstile/submit-form",
    validateInvitation: "/auth/invitations/validate",
    acceptInvitation: "/auth/invitations/accept",
    agentSignup: "/auth/agent-signup",
    generateToken: "/auth/generate-token",
    validateLink: "/auth/validate",
  },
  thryaRuntime: {
    runs: "/runtime/runs",
  },
  user: {
    getOrganizations: (email: string) => `/user/${encodeURIComponent(email)}`,
    profileEnquiry: "/user/profile-enquiry",
    updateProfile: "/user/update-profile",
  },
  whatsapp: {
    accountDetails: "/whatsapp/account-details",
    verifyAccount: "/whatsapp/test-wba-account",
    updateAccount: "/whatsapp/update-wba-details",
    phoneNumbers: "/whatsapp/phone-numbers",
    phoneNumberStatuses: "/whatsapp/phone-numbers/status",
    onboarding: "auth/whatsapponboarding",
  },
  whatsappManagement: {
    qrCodes: {
      getAll: "/message_qrdls/getall",
      create: "/message_qrdls/create",
      update: "/message_qrdls/update",
      delete: "/message_qrdls/deleteqrcode",
    },
    consent: {
      getPolicy: "/whatsapp/consent/policy",
      upsertPolicy: "/whatsapp/consent/policy",
      getKeywords: "/whatsapp/consent/keywords",
      addKeyword: "/whatsapp/consent/keywords",
      deleteKeyword: (keyword: string) =>
        `/whatsapp/consent/keywords/${encodeURIComponent(keyword)}`,
      upsertAutoReplyMessage: (consentType: string) =>
        `/whatsapp/consent/messages/${consentType}`,
      getAutoReplyMessage: (consentType: string) =>
        `/whatsapp/consent/reply/${consentType}`,
      getAllMessages: "/whatsapp/consent/reply",
      optedOutContacts: "/consents/opted-out",
      emailSuppressions: "/email/suppressions",
      removeEmailSuppression: "/email/suppressions/remove",
    },
    widget: {
      createOrUpdate: "/whatsapp-button-config/create-update",
      getAllByCIN: "/whatsapp-button-config/cin/",
      getById: "/whatsapp-button-config/get/",
      delete: "/whatsapp-button-config/button/",
    },
  },
  templates: {
    base: "/templates",
    byId: (templateId: number | string) => `/templates/${templateId}`,
    status: (templateId: number | string) => `/templates/${templateId}/status`,
    submitToMeta: "/templates/submit-to-meta",
    delete: "/templates/delete",
    uploadMediaResumable: "/upload-media/resumable",
    uploadMediaWhatsapp: "/upload-media",
    aiGenerate: "/templates/generate",
    library: "/template-library",
    syncStatus: "/templates/sync-status",
  },
  emailTemplates: {
    content: (templateId: number | string) =>
      `/email/templates/${templateId}/content`,
    testSend: "/emails/test-send",
  },
  ctwaAds: {
    fetchMapping: "/instagram-ads/CTWAMapping/fetch",
    createOrUpdateMapping: "/instagram-ads/CTWAMapping/create",
    listPages: "/instagram-ads/pages",
    listAdAccounts: "/instagram-ads/ad-accounts",
    listCampaigns: "/instagram-ads/campaigns/list",
    insights: "/instagram-ads/insights",
    campaignRelatedServices: "/instagram-ads/campaign-related-services",
    targetSearch: "/instagram-ads/target-search",
    createCampaign: "/instagram-ads/campaign",
    createAdSet: "/instagram-ads/adset",
    createAdCreative: "/instagram-ads/adcreative",
    createAd: "/instagram-ads/ad",
    uploadAdMedia: "/instagram-ads/ad-images/upload",
    uploadAdVideo: "/instagram-ads/ad-videos/upload",
    oauthAccessToken: "/instagram-ads/oauth/access-token",
    delete: "/instagram-ads/delete",
  },
  userManagement: {
    roles: {
      base: "/roles",
      byId: (roleId: number | string) => `/roles/${roleId}`,
      currentUserPermissions: "/me/permissions",
    },
    permissions: {
      system: "/permissions",
      rolePermissions: "/role-permissions",
      updateBulk: "/role-permissions/bulk",
    },
    teams: {
      base: "/teams",
      byId: (teamId: number | string) => `/teams/${teamId}`,
      users: (teamId: number | string) => `/teams/${teamId}/users`,
      manageUsers: (teamId: number | string) => `/teams/${teamId}/users/manage`,
    },
    users: {
      list: "/users/list",
      superAdmin: "/users/super-admin",
      invitations: "/users/invitations",
      invitationById: (invitationId: number | string) =>
        `/users/invitations/${invitationId}`,
      resendInvitation: (invitationId: number | string) =>
        `/users/invitations/${invitationId}/resend`,
      updateStatus: (userId: number | string, status: string) =>
        `/user/${userId}/status?status=${status}`,
    },
    projects: {
      base: "/projects",
      byId: (projectId: number | string) => `/projects/${projectId}`,
      users: "/project/users",
      userProjects: "/user/projects/list",
      members: (projectId: number | string) => `/projects/${projectId}/members`,
      manageMembers: "/projects/members",
      changeUserRole: "/project/user/role/change",
    },
  },
  messageRules: {
    enquiry: "/rule/enquiry",
    create: "/rule/create",
    keywords: "/rule/keywords",
    quickRepliesEnquiry: "/web/quick-replies-enquiry",
  },
  propertyManagement: {
    groups: "/groups",
    fields: "/fields",
    archiveProperty: "/properties/archive",
    deleteProperty: "/properties",
    bulkAssignToGroup: "/properties/groups/bulk",
    entityUsages: "/entity-usages",
  },
  crmOrders: {
    create: (projectId: number | string) => `orders/${projectId}/order`,
    byId: (projectId: number | string, orderId: number | string) =>
      `orders/${projectId}/order/${orderId}`,
    list: (projectId: number | string) => `orders/${projectId}/orders`,
    bulkStatus: (projectId: number | string) =>
      `orders/order/bulk-status/${projectId}`,
    bulkHardDelete: (projectId: number | string) =>
      `orders/bulk-hard-delete/${projectId}`,
    bulkUpsertProperty: (projectId: number | string) =>
      `orders/order/bulk-upsert-property/${projectId}`,
    property: (projectId: number | string) =>
      `orders/order/property/${projectId}`,
  },
  entityUsages: {
    base: "/entity-usages",
  },
  featureEntitlement: {
    usage: "/entitlements/usage",
  },
  conversations: {
    base: "/conversations",
    byId: (conversationId: number | string) =>
      `/conversations/${conversationId}`,
    history: (conversationId: number | string) =>
      `/conversations/${conversationId}/history`,
    status: (conversationId: number | string) =>
      `/conversations/${conversationId}/status`,
    assignAgent: (conversationId: number | string) =>
      `/conversations/${conversationId}/assign-agent`,
    bulkStatus: "/conversations/bulk/status",
    bulkAssignAgent: "/conversations/bulk/assign-agent",
    comments: (conversationId: number | string) =>
      `/conversations/${conversationId}/comments`,
    commentById: (
      conversationId: number | string,
      commentId: number | string,
    ) => `/conversations/${conversationId}/comments/${commentId}`,
    deleteComment: (commentId: number | string) =>
      `/conversations/comments/${commentId}`,
    messages: "/messages",
    sendMessage: "/messages",
    /** Single endpoint for composing, replying to, and forwarding connected email. */
    sendConnectedEmail: "/emails/connected/send",
    emailConnectedMessageAction: "/emails/connected/messages/actions",
    deleteEmailConnectedConversation: (conversationId: number | string) =>
      `/emails/connected/conversations/${conversationId}`,
    typingIndicator: "/messages/whatsapp/typing-indicator",
    unreadReset: (conversationId: number | string) =>
      `/conversations/${conversationId}/unread-reset`,
    messageStatusEvents: (messageId: number | string) =>
      `/messages/${messageId}/status-events`,
    messageStatusEventsByQuery: "/messages/status-events",
    counts: "/conversations/counts",
  },
  emailSettings: {
    footers: "/v1/email-footers",
    domains: "/email/domains",
    domainById: (domainId: number | string) => `/email/domains/${domainId}`,
    verifyDomain: (domainId: number | string) =>
      `/email/domains/${domainId}/verify`,
    activateDomain: (domainId: number | string) =>
      `/email/domains/${domainId}/activate`,
    deleteDomain: (domainId: number | string) => `/email/domains/${domainId}`,
    senders: "/email/senders",
    senderById: (senderId: number | string) => `/email/senders/${senderId}`,
    setDefaultSender: (senderId: number | string) =>
      `/email/senders/${senderId}/default`,
  },
  schema: {
    views: {
      base: "/views",
      byObjectCode: (objectCode: string) => `/views/${objectCode}`,
      byId: (viewId: number | string) => `/views/${viewId}`,
    },
    kanban: {
      cardConfig: (objectCode: string) =>
        `kanban/${encodeURIComponent(objectCode)}/card-config`,
      cardConfigProperties: (objectCode: string) =>
        `kanban/${encodeURIComponent(objectCode)}/card-config/properties`,
    },
    recordViews: {
      base: "record-views",
      byId: (recordViewId: number | string) => `record-views/${recordViewId}`,
      cards: "record-views/cards",
      cardRegistry: "record-views/cards/registry",
      upsert: "record-views/upsert",
    },
  },
  activities: {
    base: "activities",
    crmBase: "crm/activities",
    logActivity: "log-activity",
    comments: "activities/comments",
    associations: "activities/associations",
    history: "activities/history",
    tasks: "activities/tasks",
    associationSchemas: "association-schemas",
  },
  associations: {
    base: "/associations",
    bulkCreate: "/associations/bulk-create",
    schemas: "/association-schemas",
  },
  campaigns: {
    search: "/campaigns/search",
    base: "/campaigns",
    byId: (campaignId: number | string) => `/campaigns/${campaignId}`,
    audiencePreview: "/campaigns/audience/preview",
    audienceCount: "/campaigns/audience/count",
    audience: (campaignId: number | string) =>
      `/campaigns/${campaignId}/audience`,
    hardDelete: (campaignId: number | string) =>
      `/campaigns/${campaignId}/hard`,
    recipients: (campaignId: number | string) =>
      `/campaigns/${campaignId}/recipients`,
    insights: (campaignId: number | string) =>
      `/campaigns/${campaignId}/insights`,
    executions: (campaignId: number | string) =>
      `/campaigns/${campaignId}/executions`,
  },
  exports: {
    create: "/exports",
    list: "/exports",
    schedule: (exportId: number | string) => `/exports/${exportId}/schedule`,
  },
  subscription: {
    plans: "/plans",
    planById: (planId: number | string) => `/plans/${planId}`,
    planCompare: "/public/plan/compare",
    subscriptions: "/subscriptions",
    subscriptionById: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}`,
    changeSubscriptionPlan: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}/change-plan`,
    cancelSubscription: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}/cancel`,
    verifySubscriptionPayment: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}/verify-payment`,
    retrySubscriptionCheckout: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}/retry-checkout`,
    currentSubscription: "/subscriptions/current",
    billingProfile: "/subscriptions/billing-profile",
    invoices: "/invoices",
    invoiceById: (invoiceId: number | string) => `/invoices/${invoiceId}`,
    payments: "/subscriptions/payments",
  },
  pipelines: {
    base: "/pipelines",
    byId: (pipelineId: number | string) => `/pipelines/${pipelineId}`,
    stages: (pipelineId: number | string) => `/pipelines/${pipelineId}/stages`,
  },
  flowBuilder: {
    chatbots: {
      base: "/chatbot",
      byId: (chatbotId: number | string) => `/chatbot/${chatbotId}`,
      triggers: (chatbotId: number | string) =>
        `/chatbot/${chatbotId}/triggers`,
      triggerExists: "/chatbot/triggers/exists",
      settings: "/chatbot/settings",
      settingsStatus: (settingsId: number | string) =>
        `/chatbot/settings/${settingsId}/status`,
    },
    flows: {
      base: "/chatbot/flows",
      byId: (flowId: number | string) => `/chatbot/flows/${flowId}`,
      status: "/chatbot/flows/status",
      graph: "/chatbot/flows/graph",
      graphById: (flowId: number | string) => `/chatbot/flows/${flowId}/graph`,
    },
    variables: {
      base: "/chatbot/flow-variables",
      byKey: (variableKey: string) =>
        `/chatbot/flow-variables/${encodeURIComponent(variableKey)}`,
    },
    integrations: {
      platforms: "/integration-platforms",
      actions: "/integration-platforms/actions",
      actionSchema: "/integration-platforms/actions/schema",
      integrationStatus: "/integration-platforms/integration/status",
    },
  },
  flowBuilderAi: {
    capabilities: "/flow-builder/ai/capabilities",
    validate: "/flow-builder/ai/validate",
    generate: "/flow-builder/ai/generate",
    generateStart: "/flow-builder/ai/generate/start",
    generateStatus: (jobId: string) =>
      `/flow-builder/ai/generate/status/${encodeURIComponent(jobId)}`,
    generateStream: (jobId: string) =>
      `/flow-builder/ai/generate/stream/${encodeURIComponent(jobId)}`,
    generateCancel: (jobId: string) =>
      `/flow-builder/ai/generate/cancel/${encodeURIComponent(jobId)}`,
    repair: "/flow-builder/ai/repair",
  },
  triggers: {
    base: "/triggers",
    byId: (triggerId: number | string) => `/triggers/${triggerId}`,
    actions: "/triggers/actions",
    status: "/triggers/status",
    logs: "/triggers/logs",
  },
  campaignScheduler: {
    schedule: (campaignId: number | string) =>
      `/campaigns/${campaignId}/schedule`,
    byId: (campaignId: number | string) => `/campaigns/${campaignId}`,
    delete: (campaignId: number | string) => `/campaigns/${campaignId}`,
    pause: (campaignId: number | string) => `/campaigns/${campaignId}/pause`,
    resume: (campaignId: number | string) => `/campaigns/${campaignId}/resume`,
    reschedule: (campaignId: number | string) =>
      `/campaigns/${campaignId}/reschedule`,
    export: (campaignId: number | string) => `/campaigns/${campaignId}/export`,
  },
  quickReplies: {
    base: "/quick-replies",
    byId: (quickReplyId: number | string) => `/quick-replies/${quickReplyId}`,
    suggest: "/quick-replies/suggest",
  },
  notifications: {
    base: "/notifications",
    byId: (notificationId: number | string) =>
      `/notifications/${notificationId}`,
    bulkRead: "/notifications/read",
    bulkUnread: "/notifications/unread",
    bulkState: "/notifications/state",
    bulkHardDelete: "/notifications/hard",
    readById: (notificationId: number | string) =>
      `/notifications/${notificationId}/read`,
    unreadById: (notificationId: number | string) =>
      `/notifications/${notificationId}/unread`,
    readAll: "/notifications/read-all",
    unreadCount: "/notifications/unread-count",
  },
  whatsappPayments: {
    base: "/whatsapp/payment-configurations",
    byId: (configurationId: number | string) =>
      `/whatsapp/payment-configurations/${configurationId}`,
    byName: (configurationName: string) =>
      `/whatsapp/payment-configurations/name/${encodeURIComponent(configurationName)}`,
    identityCheck: "/whatsapp/payment-configurations/identityCheck",
    paymentStatusFlattenFields:
      "/whatsapp/payment-configurations/payment-status/flatten",
    paymentNotifications: "/payment-notifications",
    paymentNotificationById: (paymentStatusId: number | string) =>
      `/payment-notifications/${paymentStatusId}`,
    upsertPaymentNotification: "/payment-notifications/upsert",
    regenerateOAuth: (configurationId: number | string) =>
      `/whatsapp/payment-configurations/${configurationId}/oauth-link/regenerate`,
    action: (configurationId: number | string) =>
      `/whatsapp/payment-configurations/${configurationId}/action`,
  },
  integrationApps: {
    oauth: {
      connect: (platform: string) =>
        `/oauth/connect/${encodeURIComponent(platform)}`,
      disconnect: "/oauth/disconnect",
      platforms: "/oauth/platforms",
      supported: (platform: string) =>
        `/oauth/platforms/${encodeURIComponent(platform)}/supported`,
      userDetails: "/oauth/user-details",
    },
    emailAccounts: {
      connected: "/emails/connected/accounts",
      send: "/emails/connected/send",
      signatures: (connectedAccountId: number) =>
        `/emails/connected/${connectedAccountId}/signatures`,
      accountSignatures: (connectedAccountId: number) =>
        `/emails/connected/${connectedAccountId}/signatures`,
      signatureById: (signatureId: number) =>
        `/emails/connected/signatures/${signatureId}`,
      signatureDefault: (signatureId: number) =>
        `/emails/connected/signatures/${signatureId}/default`,
    },
    googleSpreadsheets: {
      base: "/google-apps/spreadsheets",
      byId: (spreadsheetId: string) =>
        `/google-apps/spreadsheets/${encodeURIComponent(spreadsheetId)}`,
      read: "/google-apps/spreadsheets/read",
      write: "/google-apps/spreadsheets/write",
      create: "/google-apps/spreadsheets/create",
    },
    googleCalendar: {
      calendars: "/google-calendar/calendars",
      calendarById: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}`,
      events: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}/events`,
      createEvent: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}/events`,
      eventById: (calendarId: string, eventId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
      updateEvent: (calendarId: string, eventId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
      deleteEvent: (calendarId: string, eventId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
      quickAddEvent: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}/events/quickAdd`,
      freeBusy: "/google-calendar/freebusy",
    },
    microsoftCalendar: {
      calendars: "/microsoft-calendar/calendars",
      calendarById: (calendarId: string) =>
        `/microsoft-calendar/calendars/${encodeURIComponent(calendarId)}`,
      events: (calendarId: string) =>
        `/microsoft-calendar/calendars/${encodeURIComponent(calendarId)}/events`,
      eventById: (calendarId: string, eventId: string) =>
        `/microsoft-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
    },
    microsoftTeamsMeetings: {
      base: "/microsoft-teams/meetings",
      byId: (meetingId: string) =>
        `/microsoft-teams/meetings/${encodeURIComponent(meetingId)}`,
      list: "/microsoft-teams/meetings/list",
    },
  },
  scheduling: {
    workspaces: {
      base: "/scheduling/workspaces",
      byId: (workspaceId: number | string) =>
        `/scheduling/workspaces/${workspaceId}`,
      members: (workspaceId: number | string) =>
        `/scheduling/workspaces/${workspaceId}/members`,
      membersBulk: (workspaceId: number | string) =>
        `/scheduling/workspaces/${workspaceId}/members/bulk`,
      memberById: (workspaceId: number | string, memberId: number | string) =>
        `/scheduling/workspaces/${workspaceId}/members/${memberId}`,
    },
    availabilitySchedules: {
      base: "/scheduling/availability-schedules",
      byId: (scheduleId: number | string) =>
        `/scheduling/availability-schedules/${scheduleId}`,
    },
    availabilityOverrides: {
      base: "/scheduling/availability-overrides",
      byId: (overrideId: number | string) =>
        `/scheduling/availability-overrides/${overrideId}`,
    },
    eventTypes: {
      base: "/scheduling/event-types",
      byId: (eventTypeId: number | string) =>
        `/scheduling/event-types/${eventTypeId}`,
      bySlug: (slug: string) =>
        `/scheduling/event-types/slug/${encodeURIComponent(slug)}`,
      byPublicKey: (publicKey: string) =>
        `/scheduling/event-types/public/${encodeURIComponent(publicKey)}`,
    },
    routingForms: {
      base: "/scheduling/routing-forms",
      byId: (routingFormId: number | string) =>
        `/scheduling/routing-forms/${routingFormId}`,
      publicByPublicKey: (publicKey: string) =>
        `/scheduling/routing-forms/public/${encodeURIComponent(publicKey)}`,
      publicSubmit: (publicKey: string) =>
        `/scheduling/routing-forms/${encodeURIComponent(publicKey)}/submit`,
    },
    insights: {
      bookingMemberStats: "/scheduling/insights/bookings/member-stats",
      bookingEventTrends: "/scheduling/insights/bookings/event-trends",
    },
    bookings: {
      base: "/scheduling/bookings",
      byUid: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}`,
      cancel: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/cancel`,
      confirm: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/confirm`,
      decline: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/decline`,
      markAbsent: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/mark-absent`,
      requestReschedule: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(
          bookingUid,
        )}/request-reschedule`,
      reschedule: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/reschedule`,
      postMeeting: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/post-meeting`,
      recordings: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/recordings`,
      transcripts: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/transcripts`,
      transcriptSegments: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(
          bookingUid,
        )}/transcripts/segments`,
      participants: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/participants`,
    },
    public: {
      bookingLinks: {
        byPublicKey: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(publicKey)}`,
        slots: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(publicKey)}/slots`,
        availabilityDates: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/availability-dates`,
        reserveSlot: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/reserve-slot`,
        reschedule: (publicKey: string, bookingId: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/bookings/${encodeURIComponent(bookingId)}/reschedule`,
        cancel: (publicKey: string, bookingId: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/bookings/${encodeURIComponent(bookingId)}/cancel`,
        bookingDetails: (publicKey: string, bookingId: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/bookings/${encodeURIComponent(bookingId)}`,
      },
      bookings: {
        create: "/scheduling/bookings/public",
        sendEmailVerification: "/scheduling/bookings/email-verification/send",
      },
    },
  },
  workflows: {
    /** GET — list events that can trigger a workflow */
    events: "/workflows/events",
    /** GET (list), POST (create) — managed webhook events for workflow automation */
    webhookEvents: "/webhook-events",
    /** DELETE — bulk delete managed webhook events */
    webhookEventsBulkDelete: "/webhook-events/bulk-delete",
    /** GET, PUT — single managed webhook event for workflow automation */
    webhookEvent: (eventId: number | string) => `/webhook-events/${eventId}`,
    /** PATCH — activate or disable a managed webhook event */
    webhookEventStatus: (eventId: number | string) =>
      `/webhook-events/${eventId}/status`,
    /** POST — rotate a managed webhook event endpoint token */
    webhookEventRotateToken: (eventId: number | string) =>
      `/webhook-events/${eventId}/rotate-token`,
    /** PUT — full upsert of webhook event fields */
    webhookEventFields: (eventId: number | string) =>
      `/webhook-events/${eventId}/fields`,
    /** PUT — create or update the primary CRM match rule for a webhook event */
    webhookEventMatch: (eventId: number | string) =>
      `/webhook-events/${eventId}/match`,
    /** GET — listen for the next received webhook event payload */
    webhookEventListen: (eventId: number | string) =>
      `/webhook-events/${eventId}/listen`,
    /** GET — list fields for a workflow event */
    eventFields: (eventId: number | string) =>
      `/workflows/events/${eventId}/fields`,
    /** DELETE — delete an event by id */
    byEventId: (eventId: number | string) => `/workflows/events/${eventId}`,
    /** POST — onboard a custom event + webhook action */
    onboardEvent: "/workflows/onboard-event",
    /** GET (list), POST (create) — base workflows endpoint */
    base: "/workflows",
    /** GET (by id), PATCH (update by id) */
    byId: (workflowId: number | string) => `/workflows/${workflowId}`,
    /** GET — fetch graph payload for workflow builder */
    graphById: (workflowId: number | string) => `/workflows/${workflowId}/graph`,
    /** POST — manually enroll CRM records into a workflow */
    manualEnrollments: (workflowId: number | string) =>
      `/workflows/${workflowId}/manual-enrollments`,
    /** POST — queue backend-resolved manual enrollment job */
    manualEnrollmentJobs: (workflowId: number | string) =>
      `/workflows/${workflowId}/manual-enrollment-jobs`,
    /** POST — enquiry workflow execution logs */
    executionEnquiry: "/workflows/executions/enquiry",
    /** POST — schedule workflow execution logs export */
    executionLogsExport: "/workflows/executions/export",
    /** POST — enquiry workflow action logs */
    actionLogsEnquiry: "/workflows/action-logs/enquiry",
    /** POST — schedule workflow action logs export */
    actionLogsExport: "/workflows/action-logs/export",
    /** POST — publish a workflow draft */
    publish: (workflowId: number | string) => `/workflows/${workflowId}/publish`,
    /** POST — clone a workflow into a new draft */
    clone: (workflowId: number | string) => `/workflows/${workflowId}/clone`,
    /** PUT — replace workflow review/settings payload */
    settings: (workflowId: number | string) => `/workflows/${workflowId}/settings`,
    /** GET — workflow analytics overview */
    analyticsOverview: "/workflows/analytics/overview",
    /** GET — workflow analytics comparison */
    analyticsComparison: "/workflows/analytics/comparison",
    /** GET — workflow analytics health overview */
    healthOverview: "/workflows/analytics/health/overview",
    /** PATCH — change a single workflow's status */
    status: (workflowId: number | string) => `/workflows/${workflowId}/status`,
    /** PATCH — archive / restore workflows */
    bulkSoftDelete: "/workflows/archive",
    /** DELETE — bulk hard delete workflows */
    bulkHardDelete: "/workflows/bulk",
  },
  whatsappFlows: {
    base: "/whatsapp/flows",
    list: "/whatsapp/flows",
    byId: (flowId: number | string) => `/whatsapp/flows/${flowId}`,
    create: "/whatsapp/flows/create",
    save: "/whatsapp/flows",
    /** PUT /whatsapp/flows/{flowId} — updates name, categories, flowAction, dataEndpointUrl */
    update: (flowId: number | string) => `/whatsapp/flows/${flowId}`,
    updateMetadata: "/whatsapp/flows/update/metadata",
    updateJsonMultipart: "/whatsapp/flows/update/json",
    updateJson: (flowId: number | string) => `/whatsapp/flows/${flowId}/json`,
    publish: (flowId: number | string) => `/whatsapp/flows/${flowId}/publish`,
    deprecate: (flowId: number | string) =>
      `/whatsapp/flows/${flowId}/deprecate`,
    delete: "/whatsapp/flows/delete",
    deleteById: (flowId: number | string) => `/whatsapp/flows/${flowId}`,
    responses: "/whatsapp/flows/responses",
    responsesExport: (flowId: number | string) =>
      `/whatsapp-flows/${flowId}/responses/export`,
  },
  developer: {
    apiUsage: "/developer/api-usage",
    apiClients: "/developer/api-clients",
    apiClientScopes: "/developer/api-clients/scopes",
    apiClientSecretRotate: (clientId: string) =>
      `/developer/api-clients/${encodeURIComponent(clientId)}/secrets/rotate`,
    apiClientOverview: (clientId: string) =>
      `/developer/api-clients/${encodeURIComponent(clientId)}/overview`,
    apiClientRequestLogs: (clientId: string) =>
      `/developer/api-clients/${encodeURIComponent(clientId)}/request-logs`,
    apiClientRequestLogsExport: (clientId: string) =>
      `/developer/api-clients/${encodeURIComponent(clientId)}/request-logs/export`,
    webhookDeliveriesExport: (clientId: string, webhookId: string) =>
      `/developer/api-clients/${encodeURIComponent(clientId)}/webhooks/${encodeURIComponent(webhookId)}/deliveries/export`,
    webhookCatalog: "/developer/api-clients/webhooks/catalog",
    webhookEventSample: (eventKey: string) =>
      `/developer/webhook-events/${encodeURIComponent(eventKey)}/sample`,
    webhookEventTestDelivery: (
      clientId: string,
      webhookId: string,
      eventKey: string,
    ) =>
      `/developer/api-clients/${encodeURIComponent(clientId)}/webhooks/${encodeURIComponent(webhookId)}/events/${encodeURIComponent(eventKey)}/test-delivery`,
    serviceKeys: "/developer/service-keys",
    serviceKeyById: (serviceKeyId: string) =>
      `/developer/service-keys/${serviceKeyId}`,
    serviceKeyOverview: (serviceKeyId: string) =>
      `/developer/service-keys/${serviceKeyId}/overview`,
    serviceKeyRequestLogs: (serviceKeyId: string) =>
      `/developer/service-keys/${serviceKeyId}/request-logs`,
    serviceKeyRequestLogsExport: (serviceKeyId: string) =>
      `/developer/service-keys/${encodeURIComponent(serviceKeyId)}/request-logs/export`,
    serviceKeyRotate: (serviceKeyId: string) =>
      `/developer/service-keys/${serviceKeyId}/rotate`,
    serviceKeyRevoke: (serviceKeyId: string) =>
      `/developer/service-keys/${serviceKeyId}/revoke`,
    serviceKeyRevokeAll: "/developer/service-keys/revoke-all",
  },
} as const;

// Environment types
export const ENVIRONMENTS = {
  DEV: "dev",
  PROD: "prod",
} as const;

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

// Environment-specific configurations
type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS];
const ENV: Environment =
  (import.meta.env.VITE_ENVIRONMENT as Environment) || ENVIRONMENTS.DEV;
const environmentConfigs = {
  [ENVIRONMENTS.DEV]: {
    APP_URL: "https://app-new-dev.notifyy.io",
    PUBLIC_BOOKING_URL: "https://meetings-dev-local.notifyy.io",
    BASE_URL: "https://crm-dev-local.notifyy.io/v3/api/",
    AUTH_BASE_URL: "https://auth-dev-local.notifyy.io/v3/api/",
    CONTENT_API_BASE_URL: "https://content-dev-local.notifyy.io/content/",
    OBJECT_STORAGE_URL: "https://object-dev.notifyy.io/v3/api/",
    AZURE_BLOB_BASE_URL:
      "https://notifyydevobjstorage02.blob.core.windows.net/notifyydevobjstorageblob02/",
    CAMPAIGN_BASE_URL: "https://scheduler-dev-local.notifyy.io/v3/api/",
    CRM_URL: "https://crm-dev-local.notifyy.io/v3/api/",
    PUBLIC_BASE_URL: "https://crm-dev-local.notifyy.io/v3/api/",
    META_BASE_URL: "https://graph.facebook.com/v21.0/",
    WEB_SOCKET_URL: "wss://consumer-dev-local.notifyy.io/ws-chat-gateway",
    NOTIFICATION_SOCKET_URL: "https://consumer-dev.notifyy.io/ws-notifications",
    SCHEDULER_NOTIFICATION_SOCKET_URL: "https://scheduler-dev-local.notifyy.io/v3/api/ws-job-scheduler",
    CONSUMER_BASE_URL: "https://consumer-dev-local.notifyy.io",
    AI_BASE_URL: "https://ai-dev.notifyy.io/api/v1/",
    WIDGET_SCRIPT_URL: "https://cdn.notifyy-widget.pages.dev/widget-plugin.js",
    DEBUG_MODE: true,
    API_TIMEOUT: 30000,
    AI_API_TIMEOUT: 180000,
    RETRY_ATTEMPTS: 5,
    RAZORPAY_KEY_ID: "rzp_test_EdEOG7HCX2k621",
    ENV_NAME: "Development",
  },
  [ENVIRONMENTS.PROD]: {
    // APP_URL: "https://app-new.notifyy.io",
    APP_URL: "https://app.notifyy.io",
    PUBLIC_BOOKING_URL: "https://meetings.notifyy.io",
    BASE_URL: "https://crm-dev.notifyy.io/v3/api/",
    AUTH_BASE_URL: "https://crm.notifyy.io/v3/api/",
    CONTENT_API_BASE_URL: "https://content.notifyy.io/content/",
    OBJECT_STORAGE_URL: "https://object.notifyy.io/v3/api/",
    AZURE_BLOB_BASE_URL:
      "https://notifyyprodobjstorage02.blob.core.windows.net/notifyyprodobjstorageblob02/",
    CAMPAIGN_BASE_URL: "https://scheduler.notifyy.io/v3/api/",
    CRM_URL: "https://crm-dev.notifyy.io/v3/api/",
    PUBLIC_BASE_URL: "https://crm-dev.notifyy.io/v3/api/",
    META_BASE_URL: "https://graph.facebook.com/v21.0/",
    WEB_SOCKET_URL: "wss://consumer.notifyy.io/ws-chat-gateway",
    NOTIFICATION_SOCKET_URL: "https://consumer.notifyy.io/ws-notifications",
    SCHEDULER_NOTIFICATION_SOCKET_URL: "https://scheduler.notifyy.io/v3/api/ws-job-scheduler",
    CONSUMER_BASE_URL: "https://consumer.notifyy.io",
    AI_BASE_URL: "https://ai.notifyy.io/api/v1/",
    WIDGET_SCRIPT_URL: "https://cdn.notifyy-widget.pages.dev/widget-plugin.js",
    DEBUG_MODE: false,
    API_TIMEOUT: 30000,
    AI_API_TIMEOUT: 180000,
    RETRY_ATTEMPTS: 5,
    RAZORPAY_KEY_ID: "",
    ENV_NAME: "Production",
  },
} as const;

// Current environment config
const config = environmentConfigs[ENV];
const apiBaseUrlOverride = import.meta.env.VITE_API_BASE_URL?.trim() || "";
const authApiBaseUrlOverride =
  import.meta.env.VITE_AUTH_API_BASE_URL?.trim() || "";
const contentApiBaseUrlOverride =
  import.meta.env.VITE_CONTENT_API_BASE_URL?.trim() || "";
const crmApiBaseUrlOverride =
  import.meta.env.VITE_CRM_API_BASE_URL?.trim() || "";
const contentFallbackUploadTrustedOrigins =
  import.meta.env.VITE_CONTENT_FALLBACK_UPLOAD_TRUSTED_ORIGINS?.trim() || "";
const aiApiBaseUrlOverride =
  import.meta.env.VITE_AI_API_BASE_URL?.trim() || "";

const getLocalPublicBookingOrigin = (): string => {
  if (!import.meta.env.DEV || typeof window === "undefined") return "";

  const hostname = window.location.hostname.toLowerCase();
  const isLocalHost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost");

  return isLocalHost ? "http://localhost:5173" : "";
};

const parsePositiveInteger = (
  value: string | undefined,
  fallback: number,
): number => {
  if (!value) return fallback;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallback;
};

// API Base URLs
export const API_URLS = {
  // No inferred deployment host: Commerce stays disabled until configured.
  commerce: import.meta.env.VITE_COMMERCE_API_BASE_URL?.trim() || "",
  app: config.APP_URL,
  publicBooking:
    import.meta.env.VITE_PUBLIC_BOOKING_URL ||
    getLocalPublicBookingOrigin() ||
    config.PUBLIC_BOOKING_URL,
  auth: authApiBaseUrlOverride || config.AUTH_BASE_URL,
  contentApi: contentApiBaseUrlOverride || config.CONTENT_API_BASE_URL,
  contentFallbackUploadTrustedOrigins: contentFallbackUploadTrustedOrigins
    .split(",")
    .map((origin) => origin.trim())
    .filter(Boolean),
  main: apiBaseUrlOverride || config.BASE_URL,
  public:
    import.meta.env.VITE_PUBLIC_API_BASE_URL ||
    apiBaseUrlOverride ||
    config.PUBLIC_BASE_URL,
  objectStorage: config.OBJECT_STORAGE_URL,
  azureBlob: config.AZURE_BLOB_BASE_URL,
  campaign: config.CAMPAIGN_BASE_URL,
  crm: crmApiBaseUrlOverride || apiBaseUrlOverride || config.CRM_URL,
  meta: config.META_BASE_URL,
  websocket: config.WEB_SOCKET_URL,
  notification: config.NOTIFICATION_SOCKET_URL,
  schedulerNotification: config.SCHEDULER_NOTIFICATION_SOCKET_URL,
  consumer: config.CONSUMER_BASE_URL,
  ai: aiApiBaseUrlOverride || config.AI_BASE_URL,
  widgetScript: config.WIDGET_SCRIPT_URL,
} as const;

export const getPublicBookingUrl = (
  publicKey: string,
  slug?: string | null,
): string => {
  const baseUrl = API_URLS.publicBooking.replace(/\/+$/, "");
  const normalizedPublicKey = encodeURIComponent(publicKey.trim());
  const normalizedSlug = slug?.trim()
    ? `${encodeURIComponent(slug.trim())}/`
    : "";
  return `${baseUrl}/${normalizedSlug}form/${normalizedPublicKey}`;
};

export const resolveAzureBlobFileUrl = (
  fileUrl?: string | null,
): string | null => {
  const trimmedFileUrl = fileUrl?.trim();
  if (!trimmedFileUrl) return null;

  if (/^(https?:|blob:|data:)/i.test(trimmedFileUrl)) {
    return trimmedFileUrl;
  }

  const baseUrl = API_URLS.azureBlob.replace(/\/+$/, "");
  const filePath = trimmedFileUrl.replace(/^\/+/, "");
  return encodeURI(`${baseUrl}/${filePath}`);
};

// API Settings
export const API_SETTINGS = {
  timeout: config.API_TIMEOUT,
  aiTimeout: parsePositiveInteger(
    import.meta.env.VITE_AI_API_TIMEOUT_MS,
    config.AI_API_TIMEOUT,
  ),
  retries: config.RETRY_ATTEMPTS,
  debugMode: config.DEBUG_MODE,
  envName: config.ENV_NAME,
  aiApiKey:
    import.meta.env.VITE_NOTIFY_AI_API_KEY ||
    import.meta.env.VITE_AI_API_KEY ||
    "",
} as const;

export const PAYMENT_CONFIG = {
  razorpayKeyId:
    import.meta.env.VITE_RAZORPAY_KEY_ID || config.RAZORPAY_KEY_ID,
} as const;

// Auth Retry Configuration
export const AUTH_RETRY_CONFIG = {
  /** Maximum number of retry attempts before forcing logout */
  MAX_RETRIES: 7,
  /** Initial delay between retries in milliseconds */
  INITIAL_DELAY_MS: 1000,
  /** Maximum delay between retries in milliseconds */
  MAX_DELAY_MS: 10000,
  /** Backoff multiplier for exponential delay */
  BACKOFF_MULTIPLIER: 1.5,
} as const;

// Error Messages
export const API_ERRORS = {
  NETWORK: "Unable to connect. Please check your internet.",
  TIMEOUT: "Request timed out. Please try again.",
  SERVER: "Server error. Please try again later.",
  UNAUTHORIZED: "Session expired. Please login again.",
  FORBIDDEN: "You do not have permission.",
  NOT_FOUND: "Resource not found.",
  RATE_LIMIT: "Too many requests. Please slow down.",
  UNKNOWN: "Something went wrong.",
} as const;

// Export types
export type ApiUrlKey = keyof typeof API_URLS;
export type EnvironmentConfig = typeof config;

// Export current environment
export const CURRENT_ENV = ENV;
export const IS_DEV = ENV === ENVIRONMENTS.DEV;
export const IS_PROD = ENV === ENVIRONMENTS.PROD;

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  REFRESH_TOKEN: "refresh_token",
  USER_DATA: "user_data",
  THEME: "theme",
  PERSIST_ROOT: "persist:root",
} as const;

// Security Configuration
export const SECURITY_CONFIG = {
  ENABLE_ENCRYPTION: true,
  TOKEN_STORAGE: "localStorage" as const,
} as const;

// Google OAuth Configuration
export const GOOGLE_OAUTH_CONFIG = {
  startUrl: `${API_URLS.auth}${API_ENDPOINTS.auth.googleOAuth.slice(1)}`,
} as const;

// Microsoft OAuth Configuration (MSAL)
export const MICROSOFT_OAUTH_CONFIG = {
  startUrl: `${API_URLS.auth}${API_ENDPOINTS.auth.microsoftOAuth.slice(1)}`,
} as const;

const getOAuthReturnUri = (frontendCallbackUrl?: string): string => {
  if (frontendCallbackUrl?.trim()) {
    return frontendCallbackUrl.trim();
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}/auth/callback`;
  }

  return "/auth/callback";
};

export const buildMicrosoftOAuthUrl = (
  frontendCallbackUrl?: string,
  fromPath?: string,
  useAgentCallback?: boolean,
  invitationToken?: string,
): string => {
  const params = new URLSearchParams({
    returnUri: getOAuthReturnUri(frontendCallbackUrl),
  });

  void fromPath;
  void useAgentCallback;
  void invitationToken;

  return `${MICROSOFT_OAUTH_CONFIG.startUrl}?${params.toString()}`;
};

export const buildGoogleOAuthUrl = (
  frontendCallbackUrl?: string,
  fromPath?: string,
  useAgentCallback?: boolean,
  invitationToken?: string,
): string => {
  const params = new URLSearchParams({
    returnUri: getOAuthReturnUri(frontendCallbackUrl),
  });

  void fromPath;
  void useAgentCallback;
  void invitationToken;

  return `${GOOGLE_OAUTH_CONFIG.startUrl}?${params.toString()}`;
};

export const SHOPIFY_API_KEY = "726f5bbb730de75d4dec518c1c8679a3";
